import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext.tsx';
import { ReconnectionManager, ReconnectionState } from '@/utils/reconnectionManager';

type WebSocketOptions = {
  autoReconnect?: boolean;
  reconnectInterval?: number;
  maxReconnectTime?: number;
};

const CONNECTION_TIMEOUT = 10000; // 10 seconds

/**
 * Custom hook for managing WebSocket connections with exponential backoff reconnection
 *
 * @param wsUrl
 * @param options Configuration options for the WebSocket
 * @returns Object containing WebSocket state and control functions
 */
export function useWebSocket<T>(
  wsUrl: string,
  options?: WebSocketOptions
): {
  isConnected: boolean;
  reconnect: () => void;
  close: () => void;
  addEventListener: (type: string, callback: (event: any) => void) => void,
  removeEventListener: (type: string, callbackToRemove?: (event: any) => void) => void,
  sendMessage: (message: T) => void;
  reconnectionState: ReconnectionState | null;
  error: Event | null;
} {
  // State
  const [ isConnected, setIsConnected ] = useState(false);
  const [ reconnectionState, setReconnectionState ] = useState<ReconnectionState | null>(null);
  const [ error, setError ] = useState<Event | null>(null);

  // Refs
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<number | undefined>(undefined);
  const connectionTimeoutRef = useRef<number | undefined>(undefined);
  const listenersRef = useRef<Map<string, ((event: any) => void)[]>>(new Map());
  const reconnectionManagerRef = useRef(new ReconnectionManager());
  const messageQueueRef = useRef<T[]>([]);

  const { getIdToken } = useAuth();

  // Add event listener to the WebSocket
  const addEventListener = useCallback((type: string, callback: (event: any) => void) => {
    if (!listenersRef.current.has(type)) {
      listenersRef.current.set(type, []);
    }
    const listeners = listenersRef.current.get(type);
    if (listeners) {
      listeners.push(callback);
    }
  }, []);

  // Remove event listener from the WebSocket
  const removeEventListener = useCallback((type: string, callbackToRemove?: (event: any) => void) => {
    if (!callbackToRemove) {
      // Remove all listeners for this type
      listenersRef.current.delete(type);
    } else if (listenersRef.current.has(type)) {
      // Remove specific callback
      const callbacks = listenersRef.current.get(type) || [];
      const filteredCallbacks = callbacks.filter(cb => cb !== callbackToRemove);

      if (filteredCallbacks.length > 0) {
        listenersRef.current.set(type, filteredCallbacks);
      } else {
        listenersRef.current.delete(type);
      }
    }
  }, []);

  // Reset reconnection state
  const resetReconnectionState = useCallback(() => {
    reconnectionManagerRef.current.reset();
    setError(null);
    setReconnectionState(null);
  }, []);

  // Reconnect the WebSocket
  const reconnect = useCallback(async () => {
    if (wsRef.current) {
      wsRef.current.close();
    }

    // Reset reconnection attempts when manually reconnecting
    resetReconnectionState();
    wsRef.current = new WebSocket(wsUrl);
  }, [ wsUrl, resetReconnectionState ]);

  // Close the WebSocket
  const close = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    window.clearTimeout(reconnectTimerRef.current);
    window.clearTimeout(connectionTimeoutRef.current);
    resetReconnectionState();
  }, [ resetReconnectionState ]);

  // Send messages through the WebSocket (queues if not connected)
  const sendMessage = useCallback((message: T) => {
    messageQueueRef.current.push(message);

    // Try to flush queue immediately if connected
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      while (messageQueueRef.current.length > 0) {
        const msg = messageQueueRef.current.shift();
        if (msg) {
          try {
            const serialized = JSON.stringify(msg);
            wsRef.current.send(serialized);
          } catch (err) {
            console.error('WebSocket send error:', err);
          }
        }
      }
    }
  }, []);

  // Clear queue on disconnect to drop stale messages from previous connection
  useEffect(() => {
    if (!isConnected && messageQueueRef.current.length > 0) {
      console.log(`Dropping ${messageQueueRef.current.length} stale messages from previous connection`);
      messageQueueRef.current = [];
    }
  }, [ isConnected ]);

  // Setup and manage WebSocket connection
  useEffect(() => {
    const {
      autoReconnect = false,
      reconnectInterval = 1000,
      maxReconnectTime = 30 * 60 * 1000 // Default: 30 minutes
    } = options || {};

    let isSettingUp = false;

    const setupWebSocket = async () => {
      if (isSettingUp) return;
      isSettingUp = true;

      try {
      // Clean up existing connection
        if (wsRef.current) {
          wsRef.current.close();
          wsRef.current = null;
        }
        window.clearTimeout(reconnectTimerRef.current);
        window.clearTimeout(connectionTimeoutRef.current);

        // Start tracking connection time if this is the first attempt
        reconnectionManagerRef.current.startTracking();

        // Check if we should give up trying to reconnect
        if (reconnectionManagerRef.current.shouldGiveUp(maxReconnectTime)) {
          console.error('WebSocket reconnection attempts exhausted. Giving up after 30 minutes.');
          const giveUpTime = reconnectionManagerRef.current.getGiveUpTime(maxReconnectTime);
          setReconnectionState({
            attempt: reconnectionManagerRef.current.getAttempts(),
            nextRetryTime: null,
            giveUpTime
          });
          return; // Stop trying to reconnect
        }

        const token = getIdToken();
        const protocols = token ? [ 'chat', `Bearer.${token}` ] : [ 'chat' ];

        const ws = new WebSocket(wsUrl, protocols);
        wsRef.current = ws;

        // Setup connection timeout
        connectionTimeoutRef.current = window.setTimeout(() => {
          if (ws.readyState === WebSocket.CONNECTING) {
            console.error('WebSocket connection timeout after', CONNECTION_TIMEOUT, 'ms');
            ws.close();
          // Will trigger onclose which handles reconnection
          }
        }, CONNECTION_TIMEOUT);

        // WebSocket event handler
        ws.onopen = (event) => {
        // Clear connection timeout on successful connection
          window.clearTimeout(connectionTimeoutRef.current);
          setIsConnected(true);
          resetReconnectionState();
        };

        ws.onmessage = (event) => {
          const messageListeners = listenersRef.current.get('message') || [];
          messageListeners.forEach(listener => {
            try {
              listener(event);
            } catch (error) {
              console.error('Error in message event handler:', error);
            }
          });
        };

        ws.onerror = (event) => {
          setError(event);
          console.error('WebSocket error:', event);
        };

        ws.onclose = (event) => {
          setIsConnected(false);
          window.clearTimeout(connectionTimeoutRef.current);

          const closeListeners = listenersRef.current.get('close') || [];
          closeListeners.forEach(listener => {
            try {
              listener(event);
            } catch (error) {
              console.error('Error in close event handler:', error);
            }
          });

          console.log(`WebSocket closed with code: ${event.code}, reason: ${event.reason || 'none'}`);
          // Check if closed due to authentication issues (401)
          if (event.code === 1008 || event.code === 1011) {
            console.error('WebSocket closed due to authentication or policy violation');
            // Don't attempt to reconnect automatically for auth errors
            return;
          }

          // Don't reconnect if user has logged out (no token available)
          const token = getIdToken();
          if (!token) {
            console.log('WebSocket closed and user is not authenticated. Not reconnecting.');
            return;
          }

          if (autoReconnect && !event.wasClean) {
          // Calculate delay using exponential backoff with the current attempt number
            const backoffDelay = reconnectionManagerRef.current.getNextRetryDelay(reconnectInterval);

            // Update reconnection state for UI feedback
            setReconnectionState(
              reconnectionManagerRef.current.getReconnectionState(maxReconnectTime, backoffDelay)
            );

            // Increment reconnect attempts counter
            reconnectionManagerRef.current.incrementAttempt();

            // Schedule the next reconnection attempt
            reconnectTimerRef.current = window.setTimeout(setupWebSocket, backoffDelay);

            console.log(`WebSocket reconnection attempt ${reconnectionManagerRef.current.getAttempts()} scheduled in ${Math.round(backoffDelay / 1000)}s`);
          }
        };
      } finally {
        isSettingUp = false;
      }
    };

    // Browser online/offline event handlers for immediate network change detection
    const handleOffline = () => {
      console.log('Browser detected network offline - closing WebSocket immediately');
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      setIsConnected(false);
    };

    const handleOnline = () => {
      console.log('Browser detected network online');
      const wsIsDead = !wsRef.current || wsRef.current.readyState === WebSocket.CLOSED;
      if (autoReconnect && wsIsDead) {
        console.log('Triggering immediate reconnection');
        window.clearTimeout(reconnectTimerRef.current);
        wsRef.current = null;
        // Reset reconnection state for fresh start when network comes back
        reconnectionManagerRef.current.reset();
        setReconnectionState(null);
        setupWebSocket();
      }
    };

    // Start initial connection
    setupWebSocket();

    // Listen to browser network events
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      window.clearTimeout(reconnectTimerRef.current);
      window.clearTimeout(connectionTimeoutRef.current);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, [ wsUrl, resetReconnectionState, getIdToken, options ]);

  return {
    isConnected,
    reconnect,
    close,
    addEventListener,
    removeEventListener,
    sendMessage,
    reconnectionState,
    error
  };
}
